import { Typography, useTheme } from "@mui/material";
import {
  AllSeriesType,
  AxisConfig,
  BarPlotProps,
  BarSeriesType,
  ChartsAxisContentProps,
  ChartsReferenceLine,
  ChartsXAxisProps,
  BarChart as MuiBarChart,
  PieValueType,
  ResponsiveChartContainerProps,
  ScatterValueType,
} from "@mui/x-charts";
import {
  AxisScaleConfig,
  ChartSeriesDefaultized,
  ChartSeriesType,
  ChartsYAxisProps,
  DatasetType,
  MakeOptional,
} from "@mui/x-charts/internals";
import { animated, SpringValue, to } from "@react-spring/web";
import { createElement, type ComponentProps } from "react";

import ChartTooltip from "./ChartTooltip";

// The props x-charts hands its bar-label slot. Derived from MuiBarChart's own
// `slots` type so we don't deep-import internal paths (blocked by the package's
// exports map) and don't hand-maintain a copy of the shape.
type MuiBarChartSlots = NonNullable<ComponentProps<typeof MuiBarChart>["slots"]>;
type BarLabelSlotProps = ComponentProps<NonNullable<MuiBarChartSlots["barLabel"]>>;

// x-charts centers bar labels inside the bar and has no "outside/top" position
// in v7. This slot re-renders each label just above its bar's top edge using the
// animated geometry the plot supplies: `style.y` is the bar's center and
// `style.height` its height, so `center - height/2` is the top edge. Selected
// per chart via `barLabelPosition="top"`.
const TOP_BAR_LABEL_GAP_PX = 4;

// Smallest "nice" number at least ~20% above `value`. Used to pad the y-axis so
// above-bar labels always have room (see the `barLabelPosition === "top"` axis
// handling below).
function niceMaxWithHeadroom(value: number) {
  const padded = value * 1.2;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  const step = magnitude / 2;
  return Math.ceil(padded / step) * step;
}

function TopBarLabel({ style, children }: BarLabelSlotProps) {
  if (!style) {
    return null;
  }
  const { y, height, ...rest } = style;
  // x-charts types `style` as CSSProperties but hands the slot react-spring
  // SpringValues at runtime. Narrow via `instanceof` (widening to `unknown`
  // first is not a cast) so the animated geometry is read type-safely.
  const yValue: unknown = y;
  const heightValue: unknown = height;
  if (!(yValue instanceof SpringValue) || !(heightValue instanceof SpringValue)) {
    return null;
  }
  const topY = to(
    [yValue, heightValue],
    (center, barHeight) => center - barHeight / 2 - TOP_BAR_LABEL_GAP_PX
  );
  // `animated.text`'s typing here only exposes `style`, so text-anchor/baseline
  // go through style (both are valid CSS properties) and `createElement` carries
  // the children the JSX form rejects.
  return createElement(
    animated.text,
    { style: { ...rest, textAnchor: "middle", dominantBaseline: "auto", y: topY } },
    children
  );
}

type BarChartProps = {
  dataset: DatasetType;
  series: AllSeriesType[];
  xAxis?: MakeOptional<AxisConfig<"band", any, ChartsXAxisProps>, "id">[];
  yAxis?: MakeOptional<AxisConfig<keyof AxisScaleConfig, any, ChartsYAxisProps>, "id">[];
  barLabel?: BarPlotProps["barLabel"];
  barLabelColor?: string | undefined;
  /**
   * Where the bar labels sit. "inside" (default) keeps x-charts' centered
   * labels and hides them on bars too short to fit. "top" renders each label
   * above its bar (via a custom slot) and keeps them on short bars too.
   */
  barLabelPosition?: "inside" | "top";
  referenceLines?: React.ComponentProps<typeof ChartsReferenceLine>[];
  onItemClick?: BarPlotProps["onItemClick"];
  // Set this property to use index numbers for the axis labels. The property
  // is used to display the full label in the tooltip along with the index.
  indexAxisLabelProperty?: string;
  // Set this property to use a different value for tooltip titles than the axis labels
  tooltipTitleProperty?: string;
  stackedTooltipTotalFormatter?: (total: number, dataIndex: number) => string;
  noDataMessage?: string;
  noData?: boolean;
} & ResponsiveChartContainerProps;

/**
  Wrapper component for mui x-charts bar graph which is rendered
  in a responsive container, with grid lines, tooltip, x and y axis as specified.

  Because this is housed in a responsive container, the parent container needs a height/width set.
  You can pass height/width as a prop to this component or set it on a parent.
 */
export const BarChart = ({
  dataset,
  series,
  xAxis,
  yAxis,
  barLabel,
  barLabelColor,
  barLabelPosition = "inside",
  indexAxisLabelProperty,
  tooltipTitleProperty,
  stackedTooltipTotalFormatter,
  referenceLines,
  onItemClick,
  noDataMessage,
  noData,
  ...responsiveContainerProps
}: Omit<BarChartProps, "series"> & { series: BarSeriesType[] }) => {
  const theme = useTheme();

  // Calculate the max value in the dataset
  const maxValue = Math.max(
    ...dataset.flatMap((item) =>
      series.filter(isBarSeriesType).map((s: BarSeriesType) => Number(item[s.dataKey ?? ""]) || 0)
    )
  );

  // Create a default yAxis config if max value is 0
  const defaultYAxis = [
    {
      min: 0,
      max: 10,
      tickCount: 6,
    },
  ];

  // With labels above the bars, pad the axis so the tallest bar never reaches
  // the top: x-charts clips the bar-label layer to the drawing area, so a label
  // sitting above a full-height bar would otherwise be cut off. Skip when the
  // caller pins its own max.
  // Annotating the fallback keeps `scaleType` a literal via contextual typing,
  // so no `as const` assertion is needed to satisfy the axis-config type.
  const baseYAxis: NonNullable<BarChartProps["yAxis"]> = yAxis ?? [{ scaleType: "linear" }];
  const topLabelYAxis =
    barLabelPosition === "top" && maxValue > 0
      ? baseYAxis.map((axis, index) =>
          index === 0 && axis.max == null ? { ...axis, max: niceMaxWithHeadroom(maxValue) } : axis
        )
      : yAxis;

  return (
    <MuiBarChart
      dataset={dataset.map((d, index) => {
        if (!indexAxisLabelProperty) {
          return d;
        }

        return {
          ...d,
          [indexAxisLabelProperty ?? ""]: `${index + 1}.`,
        };
      })}
      grid={{ horizontal: true }}
      series={series}
      xAxis={xAxis}
      yAxis={maxValue === 0 ? defaultYAxis : topLabelYAxis}
      {...responsiveContainerProps}
      onItemClick={onItemClick}
      barLabel={(item, context) => {
        // Short bars can't fit a centered label, so we drop it — but only for
        // inside labels. Above-the-bar labels don't overlap the bar, so short
        // bars keep theirs (zero-value bars still drop out via an empty
        // formatted string).
        if (barLabelPosition !== "top" && context.bar.height < parseFloat(theme.spacing(2))) {
          return;
        }

        if (typeof barLabel === "function") {
          return barLabel(item, context);
        }

        if (typeof barLabel === "string") {
          return item.value?.toString();
        }
      }}
      // Use mui LoadingOverlay to show a message when no data is available
      loading={noData}
      slotProps={{
        loadingOverlay: {
          message: noDataMessage,
        },
        barLabel: {
          style: {
            fill: barLabelColor,
            fontSize: "0.7rem",
          },
        },
      }}
      slots={{
        legend: () => null,
        ...(barLabelPosition === "top" ? { barLabel: TopBarLabel } : {}),
        axisContent: (params: ChartsAxisContentProps) => {
          const { dataIndex, axisValue, series } = params;

          // This should never happen, but just in case
          if (!dataIndex && dataIndex !== 0) {
            return null;
          }

          let title = axisValue?.toString() ?? "";

          if (tooltipTitleProperty) {
            title = dataset[dataIndex][tooltipTitleProperty]?.toString() ?? "";
          } else if (indexAxisLabelProperty) {
            title = dataset[dataIndex][indexAxisLabelProperty ?? ""]
              ? `${dataIndex + 1}. ${dataset[dataIndex][indexAxisLabelProperty ?? ""]?.toString()}`
              : "";
          }

          const labels = series
            .filter(isBarSeriesType)
            .map((s) => {
              const val = s.data[dataIndex];
              if (val) {
                const value = s.valueFormatter(val as number & ScatterValueType & PieValueType, {
                  dataIndex,
                });
                // A series can opt out of a tooltip row for a given point by
                // formatting to an empty string (e.g. an overlay/background
                // series with nothing meaningful to report).
                if (!value) {
                  return undefined;
                }
                return {
                  color: s.getColor(dataIndex),
                  value,
                };
              }
            })
            .filter((label): label is { color: string; value: string } => label !== undefined);

          const total = series
            .filter(isBarSeriesType)
            .map((s) => (s.data[dataIndex] as number) ?? 0)
            .reduce((partialSum, a) => partialSum + a, 0);

          const showTotal = !!stackedTooltipTotalFormatter && !!total;
          let totalLabel = undefined;
          if (showTotal) {
            totalLabel = (
              <Typography variant="body2" fontWeight="bold" sx={{ ml: 3, mt: 1 }}>
                {stackedTooltipTotalFormatter(total, dataIndex)}
              </Typography>
            );
          }

          return (
            <ChartTooltip title={title} labels={labels}>
              {totalLabel}
            </ChartTooltip>
          );
        },
      }}
      bottomAxis={{
        disableTicks: true,
        slotProps: {
          axisLine: {
            style: {
              stroke: theme.palette.divider,
              strokeWidth: 2,
            },
          },
        },
      }}
      leftAxis={{
        disableTicks: true,
        slotProps: {
          axisLine: {
            style: {
              stroke: theme.palette.divider,
              strokeWidth: 2,
            },
          },
        },
      }}
    >
      {(referenceLines ?? []).map((line, index) => (
        <ChartsReferenceLine key={index} {...line} />
      ))}
    </MuiBarChart>
  );
};

function isBarSeriesType(
  series: AllSeriesType | ChartSeriesDefaultized<ChartSeriesType>
): series is BarSeriesType {
  return series.type === "bar";
}
